import { removedLetterAnswer } from "@/helpers";
import { ENDPOINTS, getApiUrl, IApplicationState, Requests } from "@/store-redux";
import { useGetDictionary } from "@/services-react-query/queries";
import { Box, useTheme, alpha } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ToolbarSection, { PracticeTabMode } from "./components/ToolbarSection";
import MainIdeaTable from "./components/MainIdeaTable";
import ShadowingGuideDialog from "./components/ShadowingGuideDialog";

interface MainIdeaData {
  main_idea: string;
  keywords: string[];
}

const getCachedMainIdea = (sentencesList: string[]): MainIdeaData[] | null => {
  try {
    const key = `fong_main_idea_${encodeURIComponent(sentencesList.join("||"))}`;
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length === sentencesList.length) {
        return parsed;
      }
    }
  } catch (e) {
    console.error("Error reading main idea from localStorage:", e);
  }
  return null;
};

const setCachedMainIdea = (sentencesList: string[], dataList: MainIdeaData[]) => {
  try {
    const key = `fong_main_idea_${encodeURIComponent(sentencesList.join("||"))}`;
    localStorage.setItem(key, JSON.stringify(dataList));
  } catch (e) {
    console.error("Error saving main idea to localStorage:", e);
  }
};

const TabMainIdea = ({ open }: { open: boolean }) => {
  const editCustomAnswerContent = useSelector((state: IApplicationState) => state.editCustomAnswer);
  const theme = useTheme();
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  const [activeMode, setActiveMode] = useState<PracticeTabMode>("pronunciation");
  const [data, setData] = useState<MainIdeaData[]>([]);
  const [cachedSentences, setCachedSentences] = useState<string[]>([]);
  const [showWordTooltip, setShowWordTooltip] = useState(true);
  const [showVocaLevel, setShowVocaLevel] = useState(true);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [pronunciationResults, setPronunciationResults] = useState<Record<number, any>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const enablePronunciation = activeMode === "pronunciation";
  const enableShadowing = activeMode === "pronunciation";

  const sentences = useMemo(() => {
    return (editCustomAnswerContent?.data ?? "")
      .split(".")
      .filter((item) => item.trim() !== "")
      .map((item) => item.trim());
  }, [editCustomAnswerContent]);

  const wordArray = useMemo(() => {
    return removedLetterAnswer(editCustomAnswerContent?.data ?? "");
  }, [editCustomAnswerContent]);

  const genDataQuery = useMemo((): string => {
    if (wordArray?.length) {
      const dataSearch = wordArray.map((item) => {
        return item.replace(/[,.]/g, "")?.toLocaleLowerCase();
      });
      const uniqueDataSearch = [...new Set(dataSearch)];
      return uniqueDataSearch.join(",");
    }
    return "";
  }, [wordArray]);

  const { data: dataWordSearch, isFetched: isFetchedWordSearch } = useGetDictionary({
    query: {
      keyword: genDataQuery,
    },
    options: {},
    enable: Boolean(genDataQuery),
  });

  const getMainIdeaKeyword = async () => {
    if (!sentences.length) return;

    // Check LocalStorage cache first to prevent redundant API calls
    const localCached = getCachedMainIdea(sentences);
    if (localCached) {
      setData(localCached);
      setCachedSentences([...sentences]);
      return;
    }

    setData([]);
    setPronunciationResults({});
    try {
      const apiUrl = getApiUrl(ENDPOINTS.PRACTICE_GET_MAIN_IDEA);
      const data = await Requests.post(apiUrl, {
        sentences,
        prompt:
          "Hãy tóm tắt ý chính (main_idea) bằng TIẾNG VIỆT cực kỳ ngắn gọn, cô đọng (TUYỆT ĐỐI KHÔNG dịch cả câu) và cung cấp các từ khóa (keywords) giữ nguyên TIẾNG ANH cho từng câu sau đây. Ví dụ: 'I have lived in my current home for about five years now' => tóm tắt ý chính là 'Thời gian sống 5 năm'. Trả về kết quả dưới dạng JSON array.",
      });
      if (Array.isArray(data)) {
        setData(data);
        setCachedSentences([...sentences]);
        setCachedMainIdea(sentences, data);
      }
    } catch (e) {
      console.log("e", e);
    }
  };

  // Kiểm tra xem sentences có thay đổi không
  const sentencesChanged = useMemo(() => {
    if (cachedSentences.length !== sentences.length) return true;
    return cachedSentences.some((cached, index) => cached !== sentences[index]);
  }, [sentences, cachedSentences]);

  useEffect(() => {
    if (open && sentencesChanged) {
      getMainIdeaKeyword();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sentencesChanged]);

  // Handle pronunciation results
  const handlePronunciationResult = useCallback((sentenceIndex: number, result: any) => {
    setPronunciationResults((prev) => ({
      ...prev,
      [sentenceIndex]: result,
    }));
  }, []);

  const listLabels = useMemo(() => editCustomAnswerContent?.listLabels || [], [editCustomAnswerContent]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        height: "100%",
        maxHeight: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        background: isDark
          ? `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 50%), 
             radial-gradient(circle at 0% 100%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 40%),
             ${theme.palette.background.default}`
          : `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 50%), 
             radial-gradient(circle at 100% 100%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 40%),
             #f8fafc`,
        p: { xs: 1.5, md: 2.5 },
        paddingBottom: "160px",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: isDark ? "rgba(255,255,255,0.2)" : "rgba(102, 126, 234, 0.3)",
          borderRadius: "8px",
          "&:hover": {
            background: isDark ? "rgba(255,255,255,0.35)" : "rgba(102, 126, 234, 0.5)",
          },
        },
      }}
    >
      {/* 2-Option Practice Mode Selector Header */}
      <ToolbarSection
        activeMode={activeMode}
        onChangeMode={setActiveMode}
        showWordTooltip={showWordTooltip}
        showVocaLevel={showVocaLevel}
        onTooltipToggle={setShowWordTooltip}
        onVocaLevelToggle={setShowVocaLevel}
        onOpenShadowingGuide={() => setShowGuideDialog(true)}
      />

      {/* Main Content Section according to chosen mode */}
      <MainIdeaTable
        data={data}
        sentences={sentences}
        enableShadowing={enableShadowing}
        enablePronunciation={enablePronunciation}
        showWordTooltip={showWordTooltip}
        showVocaLevel={showVocaLevel}
        dataWordSearch={dataWordSearch}
        isFetchedWordSearch={isFetchedWordSearch}
        listLabels={listLabels}
        pronunciationResults={pronunciationResults}
        onPronunciationResult={handlePronunciationResult}
      />

      {/* Shadowing Guide Dialog */}
      <ShadowingGuideDialog open={showGuideDialog} onClose={() => setShowGuideDialog(false)} />
    </Box>
  );
};

export default memo(TabMainIdea);
